import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../fonts/breuer-headline.css";
import "./mode.css";
import { setBackButtonUrl } from "../uiSlice";
import { fireStageHelpSwal } from "../../functions/stageRules";
import {
  DIFFICULTY_STORAGE_KEY,
  GAME_MODE_STORAGE_KEY,
  getBackUrlFromDifficulty,
  getEnabledDifficulties,
  readSessionValue,
  writeSessionValue,
} from "../gameSetupStorage";
import RaceTitleBanner from "../components/RaceTitleBanner";

/** Static how-to-play for Endless (not themedata — in-game uses theme rules). */
export const STATIC_ENDLESS_RULES = [
  "Steer with swipe, on-screen buttons, motion, or arrow keys.",
  "The road has 3 lanes — each swipe moves one lane.",
  "Hitting obstacles costs a life. The run ends when you have no lives left.",
  "Tap the road or press Space for a Nitro boost.",
  "Score from obstacles avoided and distance travelled.",
];

export const STATIC_QUIZ_RACE_RULES = [
  "Steer with swipe, on-screen buttons, motion, or arrow keys.",
  "The road has 3 lanes — each swipe moves one lane.",
  "Cross obstacles to unlock quiz questions.",
  "Correct answers award points. Tap the road or press Space for Nitro.",
  "Wrong answers slow you down — finish all questions to complete the race.",
];

const QUIZ_MODE_ID = "quiz_runner";

export default function DifficultyMode() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);
  const { status, user } = useSelector((s) => s.auth);
  const [picking, setPicking] = useState(null);
  const enabledDifficulties = useMemo(
    () => getEnabledDifficulties(themeData),
    [themeData]
  );

  useEffect(() => {
    dispatch(setBackButtonUrl(getBackUrlFromDifficulty(themeData)));
  }, [dispatch, status, user, themeData]);

  // Quiz has no difficulty. Endless with fewer than 2 levels skips this screen.
  useEffect(() => {
    const mode = readSessionValue(GAME_MODE_STORAGE_KEY);
    if (mode === QUIZ_MODE_ID) {
      writeSessionValue(DIFFICULTY_STORAGE_KEY, "medium");
      navigate("/selection", { replace: true });
      return;
    }
    if (enabledDifficulties.length < 2) {
      writeSessionValue(
        DIFFICULTY_STORAGE_KEY,
        enabledDifficulties[0]?.id || "medium"
      );
      navigate("/selection", { replace: true });
    }
  }, [navigate, enabledDifficulties]);

  const pickDifficulty = useCallback(
    (id) => {
      if (picking) return;
      setPicking(id);
      writeSessionValue(DIFFICULTY_STORAGE_KEY, id);
      navigate("/selection", { replace: true });
    },
    [picking, navigate]
  );

  const showStaticRules = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      fireStageHelpSwal({
        themeData,
        rules: STATIC_ENDLESS_RULES,
        title: "Endless Runner",
      });
    },
    [themeData]
  );

  if (
    (themeStatus === "loading" && !themeData) ||
    readSessionValue(GAME_MODE_STORAGE_KEY) === QUIZ_MODE_ID ||
    enabledDifficulties.length < 2
  ) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div className="diff-page">
      <RaceTitleBanner>SELECT DIFFICULTY</RaceTitleBanner>
      <div className="diff-panel" role="group" aria-label="Select difficulty">
        <button
          type="button"
          className="diff-info-btn"
          aria-label="Endless Runner how to play"
          onClick={showStaticRules}
        >
          i
        </button>
        {enabledDifficulties.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={`diff-btn diff-btn--${opt.id}`}
            onClick={() => pickDifficulty(opt.id)}
            disabled={Boolean(picking)}
          >
            <span className="diff-btn__label">{opt.label}</span>
            <span className="diff-btn__check" aria-hidden />
          </button>
        ))}
      </div>
    </div>
  );
}
